import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { WalkerCardDto } from "../types/walkers";

type LoadState = "idle" | "loading" | "loaded" | "error";

const OWNER_POSTCODE_STORAGE_KEY = "wnm.ownerPostcode";

function formatLastActive(value?: string | null): string {
  if (!value) return "Recently active";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Recently active";

  const diffMs = Date.now() - d.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "Active just now";
  if (diffMs < hour) {
    const mins = Math.max(1, Math.floor(diffMs / minute));
    return `Active ${mins} min${mins === 1 ? "" : "s"} ago`;
  }
  if (diffMs < day) {
    const hours = Math.max(1, Math.floor(diffMs / hour));
    return `Active ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.max(1, Math.floor(diffMs / day));
  return `Active ${days} day${days === 1 ? "" : "s"} ago`;
}

function normalizePostcodeInput(value: string): string {
  return value.replace(/\s+/g, " ").trim().toUpperCase();
}

function readStoredOwnerPostcode(): string {
  try {
    return localStorage.getItem(OWNER_POSTCODE_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

function isLikelyFullUkPostcode(value: string): boolean {
  const normalized = normalizePostcodeInput(value);

  const fullPostcodeRegex =
    /^(GIR 0AA|[A-Z]{1,2}\d[A-Z\d]?\s\d[A-Z]{2})$/i;

  return fullPostcodeRegex.test(normalized);
}

function getPostcodeValidationMessage(value: string): string | null {
  const normalized = normalizePostcodeInput(value);

  if (!normalized) {
    return "Please enter a postcode.";
  }

  if (!isLikelyFullUkPostcode(normalized)) {
    return "Please enter a full UK postcode, for example IP1 1AA.";
  }

  return null;
}

const WalkersSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rebookMode = (searchParams.get("from") || "").trim().toLowerCase() === "rebook";
  const previousWalker = (searchParams.get("previousWalker") || "").trim();

  const initialPostcode =
    searchParams.get("postcode") ||
    (rebookMode ? readStoredOwnerPostcode() : "") ||
    "";

  const [postcode, setPostcode] = useState(initialPostcode);
  const [results, setResults] = useState<WalkerCardDto[]>([]);
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");

  const hasResults = useMemo(() => results.length > 0, [results]);

  async function runSearch(searchPostcode: string) {
    const normalized = normalizePostcodeInput(searchPostcode);
    const validationMessage = getPostcodeValidationMessage(normalized);

    if (validationMessage) {
      setError(validationMessage);
      setResults([]);
      setState("error");
      return;
    }

    try {
      setState("loading");
      setError("");

      const rows = await walkiesClient.searchWalkers(normalized, 20);

      setResults(rows);
      setState("loaded");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Search failed unexpectedly.";
      setError(message);
      setResults([]);
      setState("error");
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const normalized = normalizePostcodeInput(postcode);
    const validationMessage = getPostcodeValidationMessage(normalized);

    if (validationMessage) {
      setError(validationMessage);
      setResults([]);
      setState("error");
      return;
    }

    setPostcode(normalized);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("postcode", normalized);

    setSearchParams(nextParams);
    void runSearch(normalized);
  }

  useEffect(() => {
    const qsPostcode = searchParams.get("postcode");
    const fallbackPostcode =
      rebookMode && !qsPostcode ? readStoredOwnerPostcode() : "";
    const seed = normalizePostcodeInput(qsPostcode || fallbackPostcode || "");

    if (!seed) {
      setResults([]);
      setState("idle");
      return;
    }

    setPostcode(seed);
    void runSearch(seed);
  }, [searchParams, rebookMode]);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.backLink}>
            ← Back to home
          </Link>
        </div>

        <section style={styles.hero}>
          <div style={styles.heroBadge}>
            {rebookMode ? "Find another walker" : "Find local walkers"}
          </div>
          <h1 style={styles.h1}>
            {rebookMode
              ? "Find another trusted local dog walker"
              : "Find trusted local dog walkers"}
          </h1>
          <p style={styles.lead}>
            Search by full postcode to find dog walkers near you.
          </p>

          {rebookMode && (
            <div style={styles.rebookBanner}>
              <div style={styles.rebookBannerTitle}>
                Looking for another walker?
              </div>
              <div style={styles.rebookBannerText}>
                {previousWalker
                  ? `You can search for alternatives to ${previousWalker} and send a fresh enquiry to someone new.`
                  : "You can search for alternative walkers nearby and send a fresh enquiry to someone new."}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.searchForm}>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="Enter full postcode, e.g. IP1 1AA"
              style={styles.input}
            />
            <button
              type="submit"
              style={styles.button}
              disabled={state === "loading"}
            >
              {state === "loading" ? "Searching..." : "Search"}
            </button>
          </form>

          <p style={styles.helperText}>
            Use a full postcode such as <strong>IP1 1AA</strong>, not a partial area
            like <strong>IP9</strong>.
          </p>
        </section>

        {state === "error" && (
          <div style={styles.errorBox}>
            {error || "Something went wrong while loading walkers."}
          </div>
        )}

        {state === "loaded" && !hasResults && (
          <div style={styles.emptyBox}>
            No walkers found for that postcode yet.
          </div>
        )}

        {hasResults && (
          <section>
            <div style={styles.resultsHeader}>
              <h2 style={styles.h2}>Available walkers</h2>
              <span style={styles.resultCount}>
                {results.length} result{results.length === 1 ? "" : "s"}
              </span>
            </div>

            <div style={styles.grid}>
              {results.map((walker) => {
                const detailParams = new URLSearchParams();

                const normalizedPostcode = normalizePostcodeInput(postcode);
                if (normalizedPostcode) {
                  detailParams.set("postcode", normalizedPostcode);
                }

                if (rebookMode) {
                  detailParams.set("from", "rebook");
                }

                if (previousWalker) {
                  detailParams.set("previousWalker", previousWalker);
                }

                const detailQuery = detailParams.toString();

                return (
                  <article key={walker.walkerProfileId} style={styles.card}>
                    <div style={styles.cardBody}>
                      <div style={styles.cardBadgeRow}>
                        <span style={styles.cardBadge}>
                          {formatLastActive(walker.lastActivityUtc)}
                        </span>
                      </div>

                      <h3 style={styles.cardTitle}>{walker.displayTitle}</h3>

                      <div style={styles.meta}>
                        {walker.town && <span>{walker.town}</span>}
                        {walker.county && <span>{walker.county}</span>}
                      </div>

                      {walker.distanceMiles != null && (
                        <p style={styles.distance}>
                          {walker.distanceMiles.toFixed(1)} miles away
                        </p>
                      )}

                      <p style={styles.serviceRadius}>
                        Covers up to {walker.serviceRadiusMiles} miles
                      </p>

                      <div style={styles.actions}>
                        <Link
                          to={`/walkers/${walker.slug}${detailQuery ? `?${detailQuery}` : ""}`}
                          style={styles.linkButton}
                        >
                          View profile
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 56px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 1160,
    margin: "0 auto",
  },
  topRow: {
    marginBottom: 18,
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "rgba(255,255,255,0.9)",
    textDecoration: "none",
    fontWeight: 700,
  },
  hero: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    marginBottom: 24,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
    color: "#ffffff",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.12)",
    marginBottom: 14,
  },
  h1: {
    margin: "0 0 12px",
    fontSize: 40,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    color: "#ffffff",
  },
  lead: {
    margin: "0 0 20px",
    fontSize: 16,
    color: "rgba(255,255,255,0.8)",
    maxWidth: 720,
  },
  rebookBanner: {
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    background: "rgba(242, 138, 22, 0.16)",
    border: "1px solid rgba(242, 138, 22, 0.28)",
  },
  rebookBannerTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#ffffff",
    marginBottom: 6,
  },
  rebookBannerText: {
    fontSize: 14,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.92)",
  },
  searchForm: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  input: {
    flex: "1 1 280px",
    minWidth: 260,
    padding: "14px 16px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.2)",
    fontSize: 16,
    color: "#ffffff",
    background: "rgba(255,255,255,0.08)",
    outline: "none",
  },
  button: {
    padding: "14px 18px",
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 800,
    background: "#f28a16",
    color: "#ffffff",
  },
  helperText: {
    margin: "14px 0 0",
    fontSize: 14,
    color: "rgba(255,255,255,0.72)",
  },
  errorBox: {
    background: "rgba(155, 28, 28, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(255, 219, 219, 0.34)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 20,
  },
  emptyBox: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 18,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
    color: "#ffffff",
  },
  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    gap: 12,
  },
  h2: {
    margin: 0,
    fontSize: 28,
    color: "#ffffff",
    letterSpacing: "-0.03em",
  },
  resultCount: {
    color: "rgba(255,255,255,0.72)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },
  card: {
    background: "#f5f0e8",
    borderRadius: 22,
    boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
    overflow: "hidden",
    color: "#18201d",
  },
  cardBody: {
    padding: 18,
  },
  cardBadgeRow: {
    display: "flex",
    marginBottom: 10,
  },
  cardBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 800,
    background: "rgba(47, 79, 70, 0.08)",
    color: "#213a33",
  },
  cardTitle: {
    margin: "0 0 10px",
    fontSize: 22,
    color: "#213a33",
    lineHeight: 1.1,
  },
  meta: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    color: "rgba(24, 32, 29, 0.72)",
    marginBottom: 12,
    fontSize: 14,
  },
  distance: {
    margin: "0 0 8px",
    fontWeight: 800,
    color: "#18201d",
  },
  serviceRadius: {
    margin: "0 0 18px",
    color: "rgba(24, 32, 29, 0.72)",
  },
  actions: {
    display: "flex",
  },
  linkButton: {
    display: "inline-block",
    textDecoration: "none",
    padding: "11px 14px",
    borderRadius: 12,
    background: "#213a33",
    color: "#ffffff",
    fontWeight: 800,
  },
};

export default WalkersSearchPage;